import { LitElement, html, css, PropertyValues } from "lit";
import { customElement, property, queryAssignedElements } from "lit/decorators.js";
import type { SkyListitem } from "../sky-listitem/sky-listitem.js";
import { PresetController } from "../helper/controllers/preset-controller";
import {
  arrayPropHasChanged,
  coerceArray,
  EMPTY_ARRAY,
} from "../helper/utils/stable-array";

export type SkyListSelectionMode = "none" | "single" | "multiple";
export type SkyListChangeDetail = {
  selectedItems: SkyListitem[];
  /** Values from each selected item's `value` (empty string if unset). */
  selectedValues: string[];
};

/**
 * @element sky-list
 *
 * @summary Styled list container for `sky-listitem` with selection modes and keyboard navigation.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/list
 *
 * @uiVModel selectedValues list-change detail=selectedValues
 *
 * @slot - Default slot for `<sky-listitem>` children.
 * @property {SkyListSelectionMode} selection - Selection mode (`none`, `single`, `multiple`).
 * @property {string[]} selectedValues - Selected item `value`s (mirror for `v-model`; set each `<sky-listitem value="…">`). Default: `[]`.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @fires {CustomEvent<SkyListChangeDetail>} list-change - Fired when selected items change.
 * @method focusListItem Focuses a list item by index.
 * @method getItems Returns current slotted list items.
 *
 * @csspart list-container - The main container wrapping slotted list items.
 *
 * @Behavior
 * - Manages activation and selection state for slotted `sky-listitem` children.
 * - Supports keyboard navigation (`ArrowUp/Down`, `Home/End`, `Enter`, `Space`).
 * - Emits a normalized selected-items payload on each selection update.
 *
 * @example
 * ```html
 * <sky-list selection="single">
 *   <sky-listitem>Option A</sky-listitem>
 *   <sky-listitem>Option B</sky-listitem>
 *   <sky-listitem>Option C</sky-listitem>
 * </sky-list>
 * ```
 * ```vue
 * <template>
 *   <sky-list selection="single" @list-change="onListChange">
 *     <sky-listitem>Option A</sky-listitem>
 *     <sky-listitem>Option B</sky-listitem>
 *     <sky-listitem>Option C</sky-listitem>
 *   </sky-list>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-list selection="single">
 *       <sky-listitem>Option A</sky-listitem>
 *       <sky-listitem>Option B</sky-listitem>
 *       <sky-listitem>Option C</sky-listitem>
 *     </sky-list>
 *   );
 * }
 * ```
 */
@customElement("sky-list")
export class SkyList extends LitElement {

  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      display: block;
      font-family: var(--sky-font);
    }

    .list-container {
      width: 100%;
      border-radius: var(--sky-radius-secondary);
      overflow: hidden;
      background: var(--sky-glass-solid);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      border: var(--sky-border-primary);
      box-shadow:
        var(--sky-box-shadow-inset),
        var(--sky-box-shadow-secondary);
      isolation: isolate;
    }

    ::slotted(sky-listitem[subheader]) {
      position: sticky;
      top: 0;
      z-index: 1;
    }
  `;

  @property({ type: String }) selection: SkyListSelectionMode = "none";

  private _selectedValues: string[] = EMPTY_ARRAY as string[];

  /** Bound selection as item `value` strings (requires `value` on items). Coerces null to `[]`. */
  @property({ type: Array, hasChanged: arrayPropHasChanged })
  get selectedValues(): string[] {
    return this._selectedValues;
  }
  set selectedValues(value: string[] | null | undefined) {
    this._selectedValues = coerceArray(value);
  }

  @queryAssignedElements({ flatten: true })
  private items!: SkyListitem[];

  private currentIndex = 0;

  /** Avoid feedback when syncing selection from the `selectedValues` prop. */
  private _syncingSelectedValues = false;

  private _onItemActivate = (e: Event) => this.onItemActivate(e as CustomEvent);

  private _onSlotChange = () => {
    this._applySelectedValuesFromProp();
    this._focusFirstNavigable();
  };

  override firstUpdated(): void {
    this.syncAriaRole();
    this.addEventListener("keydown", this.onKeydown);
    this.addEventListener("item-activate", this._onItemActivate);
    this._applySelectedValuesFromProp();
    this._focusFirstNavigable();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener("keydown", this.onKeydown);
    this.removeEventListener("item-activate", this._onItemActivate);
  }

  private syncAriaRole(): void {
    if (this.selection === "none") {
      this.setAttribute("role", "list");
      this.removeAttribute("aria-multiselectable");
      return;
    }

    this.setAttribute("role", "listbox");
    if (this.selection === "multiple") {
      this.setAttribute("aria-multiselectable", "true");
    } else {
      this.removeAttribute("aria-multiselectable");
    }
  }

  private navigableItems(): SkyListitem[] {
    return (this.items ?? []).filter((item) => this.isNavigableItem(item));
  }

  private isNavigableItem(item: SkyListitem | undefined): boolean {
    return !!item && !item.disabled && !item.subheader;
  }

  private itemIndex(item: SkyListitem): number {
    return (this.items ?? []).indexOf(item);
  }

  private onItemActivate(e: CustomEvent): void {
    const item = e.detail.item as SkyListitem;
    if (!this.isNavigableItem(item)) return;

    if (this.selection === "single") {
      this.items.forEach((i) => i.setSelected(false));
      item.setSelected(true);
    } else if (this.selection === "multiple") {
      item.setSelected(!item.selected);
    }

    const selectedItems = this.items.filter((i) => i.selected);
    const selectedValues = selectedItems.map((i) => i.value || "");
    this._syncingSelectedValues = true;
    this.selectedValues = [...selectedValues];
    this._syncingSelectedValues = false;

    this.dispatchEvent(
      new CustomEvent<SkyListChangeDetail>("list-change", {
        detail: {
          selectedItems,
          selectedValues,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Applies `selectedValues` from the host to child items. */
  private _applySelectedValuesFromProp(): void {
    if (!this.items?.length || this.selection === "none") return;

    const want = new Set(this.selectedValues ?? []);
    if (this.selection === "single") {
      const first = [...want][0];
      for (const item of this.items) {
        if (item.subheader) continue;
        item.setSelected(first !== undefined && item.value === first);
      }
      return;
    }

    for (const item of this.items) {
      if (item.subheader) continue;
      item.setSelected(want.has(item.value));
    }
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);

    if (changed.has("selection")) {
      this.syncAriaRole();
    }

    if ((changed.has("selectedValues") || changed.has("selection")) && !this._syncingSelectedValues) {
      this._applySelectedValuesFromProp();
    }
  }

  private onKeydown(e: KeyboardEvent): void {
    const navigable = this.navigableItems();
    if (!navigable.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.focusStep(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        this.focusStep(-1);
        break;
      case "Home":
        e.preventDefault();
        this.focusItem(this.itemIndex(navigable[0]!));
        break;
      case "End":
        e.preventDefault();
        this.focusItem(this.itemIndex(navigable[navigable.length - 1]!));
        break;
      case " ":
      case "Enter":
        e.preventDefault();
        this.items[this.currentIndex]?.dispatchEvent(
          new CustomEvent("item-activate", {
            detail: { item: this.items[this.currentIndex] },
            bubbles: true,
            composed: true,
          }),
        );
        break;
    }
  }

  private focusStep(delta: number): void {
    const navigable = this.navigableItems();
    if (!navigable.length) return;

    const current = this.items[this.currentIndex];
    let navIdx = current ? navigable.indexOf(current) : -1;
    if (navIdx < 0) navIdx = delta > 0 ? -1 : navigable.length;

    const nextNavIdx = Math.max(0, Math.min(navIdx + delta, navigable.length - 1));
    const next = navigable[nextNavIdx];
    if (next) this.focusItem(this.itemIndex(next));
  }

  private _focusFirstNavigable(): void {
    const first = this.navigableItems()[0];
    if (first) this.focusItem(this.itemIndex(first));
  }

  private focusItem(index: number): void {
    if (!this.items?.length) return;

    const len = this.items.length;
    let next = Math.max(0, Math.min(index, len - 1));
    if (!this.isNavigableItem(this.items[next])) {
      const navigable = this.navigableItems();
      const candidate = navigable.find((item) => this.itemIndex(item) >= next) ?? navigable[0];
      if (!candidate) return;
      next = this.itemIndex(candidate);
    }

    const prev = this.items[this.currentIndex];
    if (prev) prev.shadowRoot?.querySelector(".list-item")?.setAttribute("tabindex", "-1");

    this.currentIndex = next;
    const current = this.items[this.currentIndex];
    if (current && this.isNavigableItem(current)) {
      const el = current.shadowRoot?.querySelector(".list-item") as HTMLElement | null;
      if (el) {
        el.setAttribute("tabindex", "0");
        el.focus();
      }
    }
  }

  public focusListItem(index: number): void {
    this.focusItem(index);
  }

  public getItems(): SkyListitem[] {
    return [...(this.items ?? [])];
  }

  override render() {
    return html`
      <div class="list-container" part="list-container">
        <slot @slotchange=${this._onSlotChange}></slot>
      </div>
    `;
  }
}